import React, { createElement } from 'react';
import './index.css';
import { render } from 'react-dom';
import { App } from './App';
// Simple schema for basic AI crawlers (Perplexity, etc.)
const SIMPLE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'Viridia Analytics LLC',
  alternateName: 'Viridia Analytics',
  description: 'Phoenix agency for home service businesses: The Viridia System (CRM, AI receptionist, automation), Yelp Ads management, and website design and hosting.',
  url: 'https://viridiaanalytics.com',
  telephone: '+1-623-300-0708',
  email: 'info@viridiaanalytics.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Phoenix',
    addressRegion: 'AZ',
    postalCode: '85050',
    addressCountry: 'US'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '33.7184',
    longitude: '-112.0785'
  },
  areaServed: [{
    '@type': 'City',
    name: 'Phoenix'
  }, {
    '@type': 'City',
    name: 'Scottsdale'
  }, {
    '@type': 'City',
    name: 'Mesa'
  }, {
    '@type': 'City',
    name: 'Chandler'
  }, {
    '@type': 'City',
    name: 'Glendale'
  }, {
    '@type': 'City',
    name: 'Los Angeles'
  }],
  founder: {
    '@type': 'Person',
    name: 'Kevin Rogers',
    jobTitle: 'Founder & CEO'
  },
  slogan: 'Faster Leads. More Booked Appointments. No Guesswork.',
  priceRange: '$$'
};
/* 2026-09-15 (compliance, Kevin): this file injects JSON-LD on EVERY page at
   boot, and it survived the 2026-09-08 and 09-10 cleanups. It still carried a
   "Reputation Management" service ("Automated review requests ...") and
   "reputation management" in knowsAbout. Both removed. The catalog is now the
   three services and nothing else. Do not add review or reputation claims
   here or anywhere else on the site.
   2026-09-17 (local search build): areaServed widened to the full Phoenix
   metro and the four local-search service pages added to the catalog, so
   the organization node links to them from every page. */
// Rich schema with full @graph for advanced crawlers
const STATIC_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'ProfessionalService',
    '@id': 'https://viridiaanalytics.com/#organization',
    name: 'Viridia Analytics LLC',
    alternateName: 'Viridia Analytics',
    description: 'Phoenix agency for home service businesses: The Viridia System (CRM, AI receptionist, automation), Yelp Ads management, and website design and hosting.',
    url: 'https://viridiaanalytics.com',
    telephone: '+1-623-300-0708',
    email: 'info@viridiaanalytics.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Phoenix',
      addressRegion: 'AZ',
      postalCode: '85050',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '33.7184',
      longitude: '-112.0785'
    },
    foundingDate: '2025',
    founder: {
      '@id': 'https://viridiaanalytics.com/#founder'
    },
    areaServed: [{
      '@type': 'City',
      name: 'Phoenix'
    }, {
      '@type': 'City',
      name: 'Scottsdale'
    }, {
      '@type': 'City',
      name: 'Glendale'
    }, {
      '@type': 'City',
      name: 'Peoria'
    }, {
      '@type': 'City',
      name: 'Chandler'
    }, {
      '@type': 'City',
      name: 'Mesa'
    }, {
      '@type': 'City',
      name: 'Gilbert'
    }, {
      '@type': 'City',
      name: 'Tempe'
    }, {
      '@type': 'City',
      name: 'Queen Creek'
    }, {
      '@type': 'City',
      name: 'Buckeye'
    }, {
      '@type': 'City',
      name: 'Surprise'
    }, {
      '@type': 'City',
      name: 'Goodyear'
    }, {
      '@type': 'City',
      name: 'Long Beach'
    }, {
      '@type': 'City',
      name: 'Los Angeles'
    }],
    priceRange: '$$',
    sameAs: ['https://www.facebook.com/profile.php?id=61571074363261', 'https://www.instagram.com/viridiaanalyticsllc/', 'https://www.linkedin.com/company/viridia-analytics/', 'https://www.yelp.com/biz/viridia-analytics-phoenix-2'],
    knowsAbout: ['AI lead conversion', 'AI receptionist', 'answering service for contractors', 'missed call text back', 'Yelp advertising', 'Google Ads management', 'Google Business Profile setup', 'local SEO', 'home services marketing', 'marketing automation', 'CRM systems'],
    slogan: 'Faster Leads. More Booked Appointments. No Guesswork.',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Services',
      itemListElement: [{
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'The Viridia System',
          description: 'One CRM, AI receptionist, and automation package for home service businesses: every call, text, and Yelp lead answered, follow-up, scheduling, and invoicing in one place.',
          url: 'https://viridiaanalytics.com/how-it-works'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Answering Service for Contractors',
          description: 'AI receptionist for contractors that answers every call around the clock, books estimates on the calendar and texts back missed calls.',
          url: 'https://viridiaanalytics.com/answering-service-for-contractors'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Missed-Call Text-Back',
          description: 'Automatic text back to every missed call within seconds, from a registered business number, with replies in one inbox.',
          url: 'https://viridiaanalytics.com/missed-call-text-back'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'CRM for Contractors',
          description: 'One inbox for calls, texts, Yelp and Google, a job pipeline, follow-ups that run on their own, and invoicing with QuickBooks sync.',
          url: 'https://viridiaanalytics.com/crm-for-contractors'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Google Business Profile Setup',
          description: 'One-time Google Business Profile setup and verification for Phoenix home service businesses.',
          url: 'https://viridiaanalytics.com/google-business-profile-setup'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Yelp Ads Management',
          description: 'Yelp advertising run by a former Yelp rep: ad targeting, page upgrade optimization, on-page SEO, content, and a monthly report tied to booked jobs.',
          url: 'https://viridiaanalytics.com/yelp-ads'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Website Design and Hosting',
          description: 'Fast, conversion-focused websites for local service businesses, built and hosted by Viridia.',
          url: 'https://viridiaanalytics.com/websites'
        }
      }]
    }
  }, {
    '@type': 'Person',
    '@id': 'https://viridiaanalytics.com/#founder',
    name: 'Kevin Rogers',
    jobTitle: 'Founder & CEO',
    worksFor: {
      '@id': 'https://viridiaanalytics.com/#organization'
    },
    description: 'Founder of Viridia Analytics. Former shower installer and cabinet finisher turned ad platform insider, specializing in AI-powered lead conversion and digital advertising for local service businesses and contractors.',
    sameAs: ['https://www.linkedin.com/in/kevinrogers-viridia/']
  }, {
    '@type': 'WebSite',
    '@id': 'https://viridiaanalytics.com/#website',
    name: 'Viridia Analytics',
    url: 'https://viridiaanalytics.com',
    publisher: {
      '@id': 'https://viridiaanalytics.com/#organization'
    },
    description: 'Phoenix agency helping home service businesses answer every lead: The Viridia System, Yelp Ads management, and websites.'
  }]
};
// Inject simple schema first (for basic crawlers like Perplexity)
const simpleScriptEl = document.createElement('script');
simpleScriptEl.type = 'application/ld+json';
simpleScriptEl.id = 'simple-business-schema';
simpleScriptEl.textContent = JSON.stringify(SIMPLE_SCHEMA);
document.head.appendChild(simpleScriptEl);
// Inject rich schema second (for advanced crawlers)
const richScriptEl = document.createElement('script');
richScriptEl.type = 'application/ld+json';
richScriptEl.id = 'static-business-schema';
richScriptEl.textContent = JSON.stringify(STATIC_SCHEMA);
document.head.appendChild(richScriptEl);
render(<App data-id="element-919" data-mp="thtxqj6hxaf2" />, document.getElementById('root'));